/**
 * Em que pé está o prazo de uma frente — a régua da aba "Minhas tasks".
 *
 * <p>Mora aqui, e não dentro da tela, pela mesma lição que gerou o `progressoGeral.ts`:
 * régua duplicada diverge. Se um dia o Dev Board também listar "o que é meu", ele chama
 * esta função em vez de reinventar o que é "atrasada".
 *
 * <p><b>A unidade é o mês</b>, porque é a única que a frente tem. A timeline trabalha em
 * `mesInicio`/`mesFim` — não existe dia de vencimento no modelo, e inventar um aqui seria
 * precisão falsa: uma frente que termina em setembro não vence dia 30, ela vence
 * "setembro".
 */
import type { StatusTarefa, Tarefa } from '../../api/tipos';

/**
 * {@code sem-prazo} é o card do Dev Board.
 *
 * <p>Ele não tem janela de meses — é o que o separa de uma frente —, então nenhuma das
 * outras quatro se aplica: um card não atrasa, não vence e não "vai começar". A tela
 * mostra a coluna em que ele está, que é a única resposta honesta a "em que pé está".
 */
export type Prazo =
  | 'atrasada'
  | 'vencendo'
  | 'em-andamento'
  | 'a-comecar'
  | 'sem-prazo'
  | 'concluida';

export interface PrazoDaFrente {
  prazo: Prazo;
  /** O que a pastilha escreve. */
  rotulo: string;
  /** Quantos meses faltam para o fim. Negativo é atraso; nulo quando concluída. */
  mesesRestantes: number | null;
}

const ROTULOS: Record<Prazo, string> = {
  atrasada: 'Atrasada',
  vencendo: 'Vence este mês',
  'em-andamento': 'Em andamento',
  'a-comecar': 'A começar',
  // Nunca usado: o card do Dev Board mostra o nome da coluna em que está.
  'sem-prazo': 'No Dev Board',
  concluida: 'Concluída',
};

/**
 * @param mesAtual o índice do mês corrente na timeline, ou nulo quando hoje cai fora
 *   dela. Sem mês corrente não há como falar em atraso — tudo vira "em andamento" ou o
 *   que o status disser, e a tela não inventa um prazo que o calendário não sustenta.
 */
export function prazoDaFrente(tarefa: Tarefa, mesAtual: number | null): PrazoDaFrente {
  if (tarefa.concluida) {
    return { prazo: 'concluida', rotulo: ROTULOS.concluida, mesesRestantes: null };
  }
  if (mesAtual == null) {
    const pelaApi: Prazo = tarefa.status === 'A_COMECAR' ? 'a-comecar' : 'em-andamento';
    return { prazo: pelaApi, rotulo: ROTULOS[pelaApi], mesesRestantes: null };
  }

  const restantes = tarefa.mesFim - mesAtual;

  // O status vem do servidor e é a fonte de "atrasada": ele já cruza a janela com o mês
  // corrente do projeto. Repetir a conta aqui seria a segunda régua.
  if ((tarefa.status as StatusTarefa) === 'EM_ATRASO' || restantes < 0) {
    return { prazo: 'atrasada', rotulo: ROTULOS.atrasada, mesesRestantes: restantes };
  }
  if (restantes === 0) {
    return { prazo: 'vencendo', rotulo: ROTULOS.vencendo, mesesRestantes: 0 };
  }
  if (tarefa.mesInicio > mesAtual) {
    return { prazo: 'a-comecar', rotulo: ROTULOS['a-comecar'], mesesRestantes: restantes };
  }
  return { prazo: 'em-andamento', rotulo: ROTULOS['em-andamento'], mesesRestantes: restantes };
}

/**
 * A ordem em que as frentes se leem: o que aperta primeiro.
 *
 * <p>Atrasada antes de vencendo, vencendo antes do resto, e concluída por último. É a
 * ordem da pergunta "o que eu faço agora", que é a razão de a aba existir — ordenar por
 * nome ou por bloco devolveria uma lista bonita e inútil.
 */
export const PESO: Record<Prazo, number> = {
  atrasada: 0,
  vencendo: 1,
  'em-andamento': 2,
  'a-comecar': 3,
  // Depois de tudo que tem data, e antes do que já está feito. Um card sem prazo não
  // pode passar na frente de uma frente atrasada, mas também não é assunto encerrado.
  'sem-prazo': 4,
  concluida: 5,
};
